import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Router, ResolveStart, ResolveEnd, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { environment } from '../../../../environments/environment';
import { errorFormAnimation } from '../../../shared/animations/form-error';
import { Store } from '@ngrx/store';
import { AppState } from '../../../app.state';
import { takeUntil, skip, tap, filter } from 'rxjs/operators';
import { Subject, Observable } from 'rxjs';
import { NotificationService } from 'src/app/core/services/notification/notification.service';
import { getIsAnimationVisible } from 'src/app/core/selectors/animations.selector';
import { ShowLoadAnimation, HideLoadAnimation } from 'src/app/core/actions/animations/animations.actions';
import { TranslateService } from '@ngx-translate/core';
import { InvisibleReCaptchaComponent } from 'ngx-captcha';
import {
  ForgotPasswordCaptchaAction,
  ForgotPasswordRequestLinkAction,
  ForgotPasswordRequestLinkErrorAction,
  ForgotPasswordRequestResetAction,
  ForgotPasswordRequestResetErrorAction
} from 'src/app/core/actions/forgot-password/forgot-password.actions';
import {
  getForgotPasswordCaptcha,
  getForgotPasswordRequestLinkLoading,
  getForgotPasswordRequestLinkSuccess,
  getForgotPasswordRequestLinkError,
  getForgotPasswordRequestResetLoading,
  getForgotPasswordRequestResetSuccess,
  getForgotPasswordRequestResetError
} from '../../../core/selectors/forgot-password.selectors';
import { EnvironmentResolveService } from 'src/app/core/services/environment-resolve/environment-resolve.service';

@Component({
  selector: 'app-forgot-password-page',
  templateUrl: './forgot-password-page.component.html',
  styleUrls: ['./forgot-password-page.component.scss'],
  animations: [errorFormAnimation()]
})
export class ForgotPasswordPageComponent implements OnInit, OnDestroy {
  public appVersion = environment.appVersion;
  public forgotPasswordForm: FormGroup;
  public forgotPasswordError = false;
  public isServerError = false;

  private destroy$: Subject<boolean> = new Subject<boolean>();

  public showAnimation$: Observable<boolean>;
  public isDone$: Observable<boolean>;

  public forgotPasswordRequestLinkLoading$: Observable<boolean>;
  public forgotPasswordRequestLinkSuccess$: Observable<boolean>;
  public forgotPasswordRequestResetLoading$: Observable<boolean>;
  public forgotPasswordRequestResetSuccess$: Observable<boolean>;

  private captchaResponse: string;
  public forgotPasswordToken: string;

  public readonly siteKey = this.erService.environmentERS.reCaptchaSiteKey;
  public recaptcha: any = null;
  @ViewChild('captchaElem')
  captchaElem: InvisibleReCaptchaComponent;

  public get email(): FormControl {
    return this.forgotPasswordForm.get('email') as FormControl;
  }

  public get password(): FormControl {
    return this.forgotPasswordForm.get('password') as FormControl;
  }

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private notificationService: NotificationService,
    private store: Store<AppState>,
    private translateService: TranslateService,
    private route: ActivatedRoute,
    private erService: EnvironmentResolveService
  ) {}

  ngOnInit(): void {
    this.forgotPasswordToken = this.route.snapshot.queryParamMap.get('token');

    this.router.events.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (event instanceof ResolveStart) {
        this.store.dispatch(ShowLoadAnimation());
      }
      if (event instanceof ResolveEnd) {
        this.store.dispatch(HideLoadAnimation());
      }
    });

    this.showAnimation$ = this.store.select(getIsAnimationVisible);

    this.isDone$ =
      this.store.select(getForgotPasswordRequestLinkSuccess) || this.store.select(getForgotPasswordRequestResetSuccess);

    this.forgotPasswordRequestLinkLoading$ =
      this.store.select(getForgotPasswordRequestLinkLoading) || this.store.select(getForgotPasswordCaptcha);
    this.forgotPasswordRequestLinkSuccess$ =
      this.store.select(getForgotPasswordRequestLinkSuccess) || this.store.select(getForgotPasswordCaptcha);

    this.forgotPasswordRequestResetLoading$ = this.store.select(getForgotPasswordRequestResetLoading);
    this.forgotPasswordRequestResetSuccess$ = this.store.select(getForgotPasswordRequestResetSuccess);

    this.store
      .select(getForgotPasswordRequestLinkError)
      .pipe(
        takeUntil(this.destroy$),
        filter(error => Boolean(error))
      )
      .subscribe(({ error, message }) => {
        this.isServerError = true;
        this.captchaElem.resetCaptcha();
        this.notificationService.showErrorNotification(error && error.message ? error.message : message);
      });

    this.store
      .select(getForgotPasswordRequestResetError)
      .pipe(
        takeUntil(this.destroy$),
        filter(error => Boolean(error))
      )
      .subscribe(({ error, message }) => {
        this.isServerError = true;
        this.captchaElem.resetCaptcha();
        this.notificationService.showErrorNotification(error && error.message ? error.message : message);
      });

    this.forgotPasswordForm = this.fb.group({
      email: [{ value: '', disabled: !!this.forgotPasswordToken }, [Validators.required, Validators.email]],
      password: [{ value: '', disabled: !this.forgotPasswordToken }, [Validators.required, Validators.minLength(3)]]
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  handleCaptcha(): void {
    this.store.dispatch(ForgotPasswordCaptchaAction({}));
    this.captchaElem.execute();
  }

  handleSuccess(captchaResponse: string) {
    this.captchaResponse = `${captchaResponse}`;
    if (!this.captchaResponse) {
      this.translateService.get('CAPTCHA_ARE_YOU_ROBOT').subscribe((text: string) => {
        if (this.forgotPasswordToken) {
          this.store.dispatch(ForgotPasswordRequestResetErrorAction({ error: new Error(text) }));
        } else {
          this.store.dispatch(ForgotPasswordRequestLinkErrorAction({ error: new Error(text) }));
        }
      });

      return;
    }

    if (this.forgotPasswordToken) {
      this.requestReset();
    } else {
      this.requestLink();
    }
  }

  handleExpire(): void {
    this.translateService.get('CAPTCHA_EXPIRE').subscribe((text: string) => {
      this.notificationService.showErrorNotification(text);
    });
  }

  handleError(): void {
    this.translateService.get('CAPTCHA_ERROR').subscribe((text: string) => {
      if (this.forgotPasswordToken) {
        this.store.dispatch(ForgotPasswordRequestResetErrorAction({ error: new Error(text) }));
      } else {
        this.store.dispatch(ForgotPasswordRequestLinkErrorAction({ error: new Error(text) }));
      }
    });
  }

  public requestLink(): void {
    this.store.dispatch(
      ForgotPasswordRequestLinkAction({
        captcha: this.captchaResponse,
        email: this.email.value
      })
    );
  }

  public requestReset(): void {
    this.store.dispatch(
      ForgotPasswordRequestResetAction({
        captcha: this.captchaResponse,
        token: this.forgotPasswordToken,
        password: this.password.value
      })
    );
  }
}
